import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from '@/hooks/use-toast';
import { loginSchema, validateData } from '@/utils/validation';
import { Link } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { AuthError } from '@supabase/supabase-js';
import { env } from '@/config/env';

interface LoginFormProps {
  onLoginSuccess: (email: string) => Promise<void>;
  onLoginError?: () => void;
  defaultEmail?: string;
  autoFocus?: boolean;
}

const LoginForm = ({ onLoginSuccess, onLoginError, defaultEmail = '', autoFocus = true }: LoginFormProps) => {
  const [email, setEmail] = useState(defaultEmail);
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  // Update email if defaultEmail changes
  useEffect(() => {
    if (defaultEmail) {
      setEmail(defaultEmail);
    }
  }, [defaultEmail]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setErrorMessage('');
    
    const validation = validateData(loginSchema, { email, password });
    
    if (!validation.success) {
      const errorMessages = validation.errors 
        ? validation.errors.map(issue => issue.message).join(', ')
        : 'Invalid input';
      
      toast({
        title: "Validation Error",
        description: errorMessages,
        variant: "destructive"
      });
      setErrorMessage(errorMessages);
      setIsLoading(false);
      return;
    }
    
    try {
      if (env.DEV) console.log('Attempting login with email:', email);
      
      // Standard Supabase login
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password
      });
      
      if (error) {
        if (env.DEV) console.error('Login error from Supabase:', error);
        throw error;
      }
      
      const user = data.user;
      
      if (!user) {
        console.error('Login succeeded but no user was returned');
        throw new Error('Login succeeded but no user was returned');
      }
      
      if (env.DEV) console.log('Login successful, user authenticated');
      
      // Call the success handler provided by parent component
      await onLoginSuccess(email);
      
    } catch (error: any) {
      // Format user-friendly error messages
      let userMessage = "An unexpected error occurred";
      
      if (error instanceof AuthError) {
        // Interpret common auth errors
        switch (error.message) {
          case 'Invalid login credentials':
            userMessage = 'The email or password you entered is incorrect';
            break;
          case 'Email not confirmed':
            userMessage = 'Please confirm your email address before logging in';
            break;
          case 'User not found':
            userMessage = 'No account exists with this email address';
            break;
          case 'Invalid email or password':
            userMessage = 'The email or password you entered is incorrect';
            break;
          default:
            userMessage = error.message;
        }
        
        if (env.DEV) {
          console.error('Auth error details:', {
            message: error.message,
            status: error.status,
            name: error.name
          });
        }
      } else if (error instanceof Error) {
        userMessage = error.message;
        if (env.DEV) console.error('General error during login:', error);
      }
      
      setErrorMessage(userMessage);
      toast({
        title: "Login Failed",
        description: userMessage,
        variant: "destructive"
      });
      
      // Call the onLoginError callback if provided
      if (onLoginError) {
        onLoginError();
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="email">Email</Label>
        <Input
          id="email"
          type="email"
          placeholder="name@example.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          autoFocus={autoFocus}
          autoCapitalize="none"
          autoComplete="email"
          autoCorrect="off"
        />
      </div>
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label htmlFor="password">Password</Label>
          <Link to="/reset-password" className="text-sm text-coaching-primary hover:underline">
            Forgot password?
          </Link>
        </div>
        <Input
          id="password"
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          autoFocus={!email && autoFocus}
          autoComplete="current-password"
        />
      </div>
      <Button
        type="submit"
        className="w-full bg-coaching-primary hover:bg-coaching-primary/90"
        disabled={isLoading}
      >
        {isLoading ? "Signing in..." : "Sign In"}
      </Button>
      
      {errorMessage && (
        <div className="text-sm text-red-500 mt-2">{errorMessage}</div>
      )}
    </form>
  );
};

export default LoginForm;
